/**
 * URL Encoder/Decoder View
 * Interactive URL encoding and decoding
 */
import React, { useState } from 'react';
import { Box, Text, useInput } from 'ink';
import TextInput from 'ink-text-input';
import { UtilsService } from '../../../services/utils.service.js';

type Mode = 'encode' | 'decode';

export const UrlView: React.FC = () => {
    const [input, setInput] = useState('');
    const [mode, setMode] = useState<Mode>('encode');
    const [fullUrl, setFullUrl] = useState(false);
    const [result, setResult] = useState('');
    const [focusedField, setFocusedField] = useState<'input' | 'mode' | 'full' | 'convert'>('input');
    const [copied, setCopied] = useState(false);

    const service = new UtilsService();

    useInput((input, key) => {
        if (key.tab) {
            const fields: typeof focusedField[] = ['input', 'mode', 'full', 'convert'];
            const currentIndex = fields.indexOf(focusedField);
            setFocusedField(fields[(currentIndex + 1) % fields.length]);
        } else if (key.return) {
            if (focusedField === 'mode') {
                setMode(mode === 'encode' ? 'decode' : 'encode');
                setResult('');
            } else if (focusedField === 'full') {
                setFullUrl(!fullUrl);
            } else {
                // Convert on Enter from input or convert button
                handleConvert();
            }
        } else if (input === 'c' && result) {
            handleCopy();
        }
    });

    const handleConvert = () => {
        if (!input) return;

        if (mode === 'encode') {
            const encoded = fullUrl
                ? service.urlEncode(input, true)
                : service.urlEncode(input, false);
            setResult(encoded);
        } else {
            const decoded = fullUrl
                ? service.urlDecode(input, true)
                : service.urlDecode(input, false);
            setResult(decoded);
        }
        setCopied(false);
    };

    const handleCopy = async () => {
        try {
            const { default: clipboardy } = await import('clipboardy');
            await clipboardy.write(result);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // Clipboard not available
        }
    };

    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    🔗 URL Encoder/Decoder
                </Text>
            </Box>

            <Box
                flexDirection="column"
                borderStyle="single"
                borderColor="gray"
                paddingX={2}
                paddingY={1}
                marginBottom={1}
            >
                <Text bold color="yellow" marginBottom={1}>
                    Options:
                </Text>

                <Box marginBottom={1} flexDirection="column">
                    <Box marginBottom={0}>
                        <Text color={focusedField === 'input' ? 'green' : undefined}>
                            {focusedField === 'input' ? '▶ ' : '  '}Input text:
                        </Text>
                    </Box>
                    <Box marginLeft={2} width={60}>
                        {focusedField === 'input' ? (
                            <TextInput value={input} onChange={setInput} placeholder="Enter text or URL..." />
                        ) : (
                            <Text dimColor={!input}>{input || '(empty)'}</Text>
                        )}
                    </Box>
                </Box>

                <Box marginBottom={1}>
                    <Box width={20}>
                        <Text color={focusedField === 'mode' ? 'green' : undefined}>
                            {focusedField === 'mode' ? '▶ ' : '  '}Mode:
                        </Text>
                    </Box>
                    <Text bold color={focusedField === 'mode' ? 'yellow' : undefined}>
                        {mode === 'encode' ? 'ENCODE' : 'DECODE'}
                    </Text>
                    {focusedField === 'mode' && (
                        <Text dimColor> (Enter to toggle)</Text>
                    )}
                </Box>

                <Box marginBottom={1}>
                    <Text color={focusedField === 'full' ? 'green' : undefined}>
                        {focusedField === 'full' ? '▶ ' : '  '}
                        [{fullUrl ? '✓' : ' '}] Full URL (encode/decode entire URL)
                    </Text>
                </Box>

                <Box marginTop={1}>
                    <Text
                        bold
                        backgroundColor={focusedField === 'convert' ? 'green' : undefined}
                        color={focusedField === 'convert' ? 'black' : 'green'}
                    >
                        {focusedField === 'convert' ? '▶ ' : '  '}
                        [ {mode === 'encode' ? 'Encode' : 'Decode'} ]
                    </Text>
                </Box>
            </Box>

            {result && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="green"
                    paddingX={2}
                    paddingY={1}
                >
                    <Box marginBottom={1}>
                        <Text bold color="green">
                            ✓ Result:
                        </Text>
                        {copied && (
                            <Box marginLeft={2}>
                                <Text color="green">✓ Copied to clipboard!</Text>
                            </Box>
                        )}
                    </Box>

                    <Box flexDirection="column">
                        <Text dimColor>Mode: {mode.toUpperCase()} {fullUrl ? '(Full URL)' : '(Component)'}</Text>
                        <Box marginTop={1}>
                            <Text wrap="wrap">{result}</Text>
                        </Box>
                    </Box>
                </Box>
            )}

            <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={2}>
                <Text dimColor>
                    Tab: Next | Enter: Toggle/Convert | C: Copy | Esc: Back
                </Text>
            </Box>
        </Box>
    );
};
